import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { useAuthStore } from '@/stores/auth';
import * as api from './api';
export { useInfoHintPulse } from './hooks/useInfoHintPulse';

function useActiveTenantId(): string {
  const tenantId = useAuthStore((s) => s.tenantUser?.tenantId);
  if (!tenantId) throw new Error('useActiveTenantId called without an active tenant');
  return tenantId;
}

export function useDismissWelcome() {
  const setWelcomed = useAuthStore((s) => s.setWelcomed);
  return useMutation({
    mutationFn: () => api.dismissWelcome(),
    onSuccess: () => setWelcomed(new Date().toISOString()),
  });
}

export function useDismissCelebration(memberId: string) {
  const tenantId = useActiveTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.dismissCelebration(tenantId, memberId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['member', tenantId, memberId] }),
  });
}

export function useRecommendedJobs(memberId: string | null) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ['recommended-jobs', tenantId, memberId] as const,
    queryFn: () => api.getRecommendedJobs(tenantId, memberId!),
    enabled: Boolean(memberId),
  });
}

export function useSystemDepartments() {
  return useQuery({
    queryKey: ['system-departments'] as const,
    queryFn: () => api.listSystemDepartments(),
    staleTime: 60 * 60 * 1000, // 1h — departments rarely change
  });
}

export function useStarterPreview(departmentIds: string[]) {
  const [debouncedIds, setDebouncedIds] = useState(departmentIds);
  const idsKey = departmentIds.join(',');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedIds(departmentIds), 200);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  return useQuery({
    queryKey: ['starter-preview', debouncedIds] as const,
    queryFn: () => api.getStarterPreview(debouncedIds),
    staleTime: 60 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}
